// src/components/AssessmentProgressIndicator.js
// Dynamic assessment progress indicator with informative horizontal layout

import React from 'react';
import { useSelector } from 'react-redux';
import { 
  Card, Text, Stack, Group, Badge, 
  ActionIcon, Divider, Tooltip, HoverCard
} from '@mantine/core';
import { 
  IconProgressBolt, IconMenu2, IconArrowRight,
  IconClipboard, IconAlertTriangle, IconLock, IconChartBar,
  IconClock, IconX, IconInfoCircle
} from '@tabler/icons-react';
// No Redux actions needed for this read-only component

// Convert camelCase or snake_case strings to Title Case
const toTitleCase = (str) => {
  if (!str) return '';
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Phase mapping based on assessment flow
const getPhaseInfo = (stepLabel, subStep, phase) => {
  // Use phase from Redux if available (for phase-based system)
  if (phase) {
    if (phase === 'source_processing') {
      return {
        phase: 'Risk Discovery',
        phaseColor: 'blue',
        description: 'Identifying and analyzing risk sources'
      };
    } else if (phase === 'risk_evaluation') {
      return {
        phase: 'Risk Evaluation', 
        phaseColor: 'orange',
        description: 'Evaluating and planning mitigation'
      };
    } else if (phase === 'complete') {
      return {
        phase: 'Monitoring & Reporting', 
        phaseColor: 'green',
        description: 'Assessment reporting and documentation'
      };
    }
  }
  
  // Fallback to step-based phase detection
  if (stepLabel === 'useCase') {
    return {
      phase: 'Risk Discovery',
      phaseColor: 'blue', 
      description: 'Setting context and identifying risks'
    };
  } else if (stepLabel === 'safetyRisks') {
    // Determine sub-phase within safety risks
    const discoverySteps = ['intro', 'sources', 'cause', 'analysis'];
    const evaluationSteps = ['eval', 'actionPlan', 'controlEval', 'residual', 'summary'];
    
    if (discoverySteps.includes(subStep)) {
      return {
        phase: 'Risk Discovery',
        phaseColor: 'blue',
        description: 'Identifying and analyzing risk sources'
      };
    } else if (evaluationSteps.includes(subStep)) {
      return {
        phase: 'Risk Evaluation',
        phaseColor: 'orange', 
        description: 'Evaluating and planning mitigation'
      };
    } else {
      // Default to Risk Discovery for safety risks
      return {
        phase: 'Risk Discovery',
        phaseColor: 'blue',
        description: 'Identifying and analyzing risk sources'
      };
    }
  } else if (stepLabel === 'securityAssessment') {
    return {
      phase: 'Risk Discovery',
      phaseColor: 'blue',
      description: 'Security threat modeling and analysis'
    };
  } else if (stepLabel === 'compliance') {
    return {
      phase: 'Risk Evaluation',
      phaseColor: 'orange',
      description: 'Compliance assessment and reporting'
    };
  } else if (stepLabel === 'assessmentSummary') {
    return {
      phase: 'Monitoring & Reporting',
      phaseColor: 'green',
      description: 'Final reporting and documentation'
    };
  }
  
  // Default fallback
  return {
    phase: 'Risk Discovery',
    phaseColor: 'blue',
    description: 'Assessment in progress'
  };
};

// Step titles mapping
const STEP_TITLES = {
  useCase: 'Use Case & Context',
  safetyRisks: 'Safety Risk Assessment', 
  securityAssessment: 'Security Assessment',
  compliance: 'Compliance Review',
  assessmentSummary: 'Assessment Summary'
};

export default function AssessmentProgressIndicator({
  // Current position (for fallback compatibility)
  activeStep,
  activeSectionIndex,
  activeSourceIndex,
  activeSourceName,
  
  // Structure data
  steps,
  totalSteps,
  
  // Progress data
  riskFormData,
  contextData,
  
  // Model info
  modelName,
  modelId,
  assessmentUuid, // Add assessmentUuid for persistence
  
  // Menu handler
  onMenuClick,
  
  // Style props
  variant = 'default' // 'default' | 'compact'
}) {
  // Get actual position from Redux state
  const savedPosition = useSelector(s => s.risks.byAssessment[modelId]?.position);
  
  // Use actual position if available, otherwise fall back to props
  const actualActiveStep = savedPosition?.majorStep === 'useCase' ? 0 : 
    steps.findIndex(s => s.label === savedPosition?.majorStep) >= 0 ? 
    steps.findIndex(s => s.label === savedPosition?.majorStep) : activeStep;
  
  const actualActiveSectionIndex = savedPosition?.section ? 
    steps[actualActiveStep]?.sections?.indexOf(savedPosition.section) >= 0 ?
    steps[actualActiveStep]?.sections?.indexOf(savedPosition.section) : activeSectionIndex
    : activeSectionIndex;
  
  const actualActiveSourceName = savedPosition?.sourceName || activeSourceName;
  const actualSubStep = savedPosition?.subStep;

  // Get detailed current evaluation info for card-based layout
  const getCurrentEvaluationDetails = () => {
    const currentStep = steps[actualActiveStep];
    if (!currentStep) return null;

    const currentStepLabel = currentStep.label;
    const currentSectionKey = currentStep.sections?.[actualActiveSectionIndex];

    if (currentStepLabel === 'useCase') {
      const phaseInfo = getPhaseInfo(currentStepLabel);
      return {
        type: 'context',
        icon: <IconClipboard size={18} color="#495057" />,
        title: 'Context Section',
        area: toTitleCase(currentSectionKey),
        position: `${actualActiveSectionIndex + 1}/${currentStep.sections.length}`,
        currentStep: contextData[currentSectionKey]?.narrative ? 'Completed' : 'In Progress',
        nextStep: actualActiveSectionIndex < currentStep.sections.length - 1 
          ? toTitleCase(currentStep.sections[actualActiveSectionIndex + 1])
          : 'Safety Risk Assessment',
        phase: phaseInfo.phase,
        phaseColor: phaseInfo.phaseColor,
        phaseDescription: phaseInfo.description
      };
    }

    if (currentStepLabel === 'safetyRisks') {
      // Get section data to validate saved position and provide fallbacks
      const sectionData = riskFormData[currentSectionKey] || {};
      const totalSections = currentStep.sections.length;
      const completedSections = currentStep.sections.filter(key => 
        riskFormData[key]?.status === 'Complete' || riskFormData[key]?.status === 'NA'
      ).length;

      // Map risk flow steps to user-friendly names
      const stepNames = {
        'intro': 'Introduction',
        'sources': 'Select Sources',
        'cause': 'Cause Analysis',
        'analysis': 'Risk Analysis', 
        'eval': 'Risk Evaluation',
        'actionPlan': 'Action Planning',
        'controlEval': 'Control Evaluation',
        'residual': 'Residual Risk',
        'summary': 'Summary'
      };

      const currentSubStepKey = actualSubStep || 'intro';
      const currentSubStep = stepNames[currentSubStepKey] || currentSubStepKey;
      
      // Get phase from saved position (always use phase-based system now)
      const phase = savedPosition?.phase;
      
      // Get phase information
      const phaseInfo = getPhaseInfo(currentStepLabel, currentSubStepKey, phase);

      // Determine next step based on phase
      let nextSubStep = 'Complete';
      if (phase === 'source_processing') {
        // Phase 1: Source processing (cause -> analysis)
        const sourceStepOrder = ['cause', 'analysis'];
        const currentIndex = sourceStepOrder.indexOf(currentSubStepKey);
        nextSubStep = currentIndex >= 0 && currentIndex < sourceStepOrder.length - 1 ?
          stepNames[sourceStepOrder[currentIndex + 1]] : 'Risk Evaluation';
      } else if (phase === 'risk_evaluation') {
        // Phase 2: Risk processing (eval -> residual)
        const riskStepOrder = ['eval', 'actionPlan', 'controlEval', 'residual'];
        const currentIndex = riskStepOrder.indexOf(currentSubStepKey);
        nextSubStep = currentIndex >= 0 && currentIndex < riskStepOrder.length - 1 ?
          stepNames[riskStepOrder[currentIndex + 1]] : 'Complete';
      } else if (phase === 'complete') {
        nextSubStep = 'Assessment Complete';
      } else {
        // Fallback: determine step based on current sub-step if no phase available
        const allSteps = ['intro', 'sources', 'cause', 'analysis', 'eval', 'actionPlan', 'controlEval', 'residual'];
        const currentIndex = allSteps.indexOf(currentSubStepKey);
        if (currentIndex >= 0 && currentIndex < allSteps.length - 1) {
          nextSubStep = stepNames[allSteps[currentIndex + 1]];
        }
      }

      // Determine source/risk info based on phase
      let sourceInfo = { source: actualActiveSourceName, sourcePosition: null };
      
      if (phase === 'source_processing') {
        // Show source info during source processing - validate against actual sources
        const availableSources = sectionData.riskSources?.map(s => s.name) || [];
        const sourceName = savedPosition?.sourceName && availableSources.includes(savedPosition.sourceName) 
          ? savedPosition.sourceName 
          : actualActiveSourceName || availableSources[0] || 'Processing Source';
        
        sourceInfo = {
          source: sourceName,
          sourcePosition: null, // Could add back later: availableSources.length > 1 ? `1/${availableSources.length}` : null,
          isRisk: false
        };
      } else if (phase === 'risk_evaluation') {
        // Show risk info during risk processing - validate against actual risks
        const availableRisks = Object.values(sectionData.areaRisks || {}).flat() || [];
        const riskName = savedPosition?.riskName && availableRisks.find(r => r.riskName === savedPosition.riskName)
          ? savedPosition.riskName
          : availableRisks[0]?.riskName || 'Processing Risk';
          
        sourceInfo = {
          source: riskName,
          sourcePosition: null, // Could add back later: availableRisks.length > 1 ? `1/${availableRisks.length}` : null,
          isRisk: true
        };
      } else if (phase === 'complete') {
        // Show completion status with actual counts
        const totalRisks = Object.values(sectionData.areaRisks || {}).flat().length;
        sourceInfo = {
          source: totalRisks > 0 ? `${totalRisks} Risk${totalRisks !== 1 ? 's' : ''} Complete` : 'Assessment Complete',
          sourcePosition: null,
          isRisk: false
        };
      } else {
        // Fallback for when no phase is set (initial states)
        sourceInfo = {
          source: actualActiveSourceName || 'Getting Started',
          sourcePosition: null,
          isRisk: false
        };
      }

      return {
        type: 'safety',
        icon: <IconAlertTriangle size={18} color="#fa5252" />,
        title: 'Risk Area',
        area: toTitleCase(currentSectionKey),
        position: `${completedSections + 1}/${totalSections}`,
        source: sourceInfo.source,
        sourcePosition: sourceInfo.sourcePosition,
        isRisk: sourceInfo.isRisk || false,
        currentStep: currentSubStep,
        nextStep: nextSubStep,
        phase: phaseInfo.phase,
        phaseColor: phaseInfo.phaseColor,
        phaseDescription: phaseInfo.description
      };
    }

    if (currentStepLabel === 'securityAssessment') {
      // Read scenario-based position from savedPosition set by SecurityRiskAssessment
      const scenarioIndex = savedPosition?.scenarioIndex || null;
      const scenarioCount = savedPosition?.scenarioCount || null;
      const scenarioName  = savedPosition?.scenarioName || 'Select Scenario';
      const sub = savedPosition?.securitySubStep || 'selectScenario';
      const subLabelMap = {
        selectScenario: 'Select Scenario',
        attributes: 'Attributes',
        'agent-mapping': 'Threat Agents',
        'attack-surface': 'Attack Surface',
        impact: 'Impact',
        'action-plan': 'Action Plan',
      };
      const subOrder = ['selectScenario','attributes','agent-mapping','attack-surface','impact','action-plan'];
      const currentIdx = subOrder.indexOf(sub);
      const nextSub = currentIdx >= 0 && currentIdx < subOrder.length - 1 ? subLabelMap[subOrder[currentIdx + 1]] : 'Complete';

      const phaseInfo = getPhaseInfo(currentStepLabel);
      return {
        type: 'security',
        icon: <IconLock size={18} color="#495057" />,
        title: 'Security Assessment',
        area: 'Security Threat Modelling',
        position: null,
        source: scenarioName,
        sourcePosition: scenarioIndex && scenarioCount ? `${scenarioIndex}/${scenarioCount}` : null,
        currentStep: subLabelMap[sub] || 'Select Scenario',
        nextStep: nextSub,
        phase: phaseInfo.phase,
        phaseColor: phaseInfo.phaseColor,
        phaseDescription: phaseInfo.description
      };
    }

    const phaseInfo = getPhaseInfo(currentStepLabel);
    return {
      type: 'general',
      icon: <IconChartBar size={18} color="#495057" />,
      title: 'Assessment Step',
      area: STEP_TITLES[currentStepLabel] || 'Processing',
      position: `${actualActiveStep + 1}/${totalSteps}`,
      currentStep: 'In Progress',
      nextStep: actualActiveStep < totalSteps - 1 ? STEP_TITLES[steps[actualActiveStep + 1]?.label] : 'Complete',
      phase: phaseInfo.phase,
      phaseColor: phaseInfo.phaseColor,
      phaseDescription: phaseInfo.description
    };
  };

  const evaluationDetails = getCurrentEvaluationDetails();
  const currentStepTitle = STEP_TITLES[steps[actualActiveStep]?.label] || 'Assessment';

  // Get current area status for integrated display
  const getCurrentAreaStatus = () => {
    if (!evaluationDetails || evaluationDetails.type !== 'safety') return null;
    
    const currentStep = steps[actualActiveStep];
    const currentSectionKey = currentStep.sections?.[actualActiveSectionIndex];
    const currentAreaStatus = riskFormData[currentSectionKey]?.status;
    
    return {
      currentIndex: actualActiveSectionIndex + 1,
      totalAreas: currentStep.sections.length,
      status: currentAreaStatus,
      allAreas: currentStep.sections.map((areaKey, index) => ({
        name: toTitleCase(areaKey),
        key: areaKey,
        status: riskFormData[areaKey]?.status,
        isActive: index === actualActiveSectionIndex,
        position: index + 1
      }))
    };
  };

  const currentAreaInfo = getCurrentAreaStatus();

  // Get current sources status for expandable display
  const getCurrentSourcesStatus = () => {
    if (!evaluationDetails || evaluationDetails.type !== 'safety') return null;
    
    const currentStep = steps[actualActiveStep];
    const currentSectionKey = currentStep.sections?.[actualActiveSectionIndex];
    const sectionData = riskFormData[currentSectionKey] || {};
    const availableSources = sectionData.riskSources || [];
    
    if (availableSources.length === 0) return null;
    
    const currentSourceName = savedPosition?.sourceName || actualActiveSourceName;
    const currentSourceIndex = availableSources.findIndex(s => s.name === currentSourceName);
    
    return {
      currentIndex: Math.max(0, currentSourceIndex) + 1,
      totalSources: availableSources.length,
      currentSource: currentSourceName,
      allSources: availableSources.map((source, index) => ({
        name: source.name,
        key: source.name,
        status: index < currentSourceIndex ? 'completed' : 
                index === currentSourceIndex ? 'current' : 'pending',
        isActive: index === currentSourceIndex,
        position: index + 1
      }))
    };
  };

  // Get current risks status for expandable display
  const getCurrentRisksStatus = () => {
    if (!evaluationDetails || evaluationDetails.type !== 'safety') return null;
    
    const currentStep = steps[actualActiveStep];
    const currentSectionKey = currentStep.sections?.[actualActiveSectionIndex];
    const sectionData = riskFormData[currentSectionKey] || {};
    const availableRisks = Object.values(sectionData.areaRisks || {}).flat() || [];
    
    if (availableRisks.length === 0) return null;
    
    const currentRiskName = savedPosition?.riskName;
    const currentRiskIndex = availableRisks.findIndex(r => r.riskName === currentRiskName);
    
    return {
      currentIndex: Math.max(0, currentRiskIndex) + 1,
      totalRisks: availableRisks.length,
      currentRisk: currentRiskName,
      allRisks: availableRisks.map((risk, index) => ({
        name: risk.riskName || `Risk ${index + 1}`,
        key: risk.riskId,
        status: index < currentRiskIndex ? 'completed' : 
                index === currentRiskIndex ? 'current' : 'pending',
        isActive: index === currentRiskIndex,
        position: index + 1
      }))
    };
  };

  const currentSourcesInfo = getCurrentSourcesStatus();
  const currentRisksInfo = getCurrentRisksStatus();

  // Helper to get status icon and color
  const getStatusIcon = (status) => {
    switch (status) {
      case 'DEFERRED':
        return { icon: <IconClock size={14} />, color: '#ff8c00' }; // Orange
      case 'NA':
        return { icon: <IconX size={14} />, color: '#6c757d' }; // Gray
      default:
        return null;
    }
  };

  // Legacy getAreaStatistics and StatBadge components removed - now using integrated approach in hover tooltip

  // AreaStatistics component removed - now using direct StatBadge components

  if (variant === 'compact') {
    return (
      <Card withBorder radius="md" p="md" style={{ backgroundColor: '#f8f9fa' }}>
        <Stack spacing="sm">
          <Group position="apart" align="center">
            <Group spacing="sm">
              <IconProgressBolt size={24} color="#495057" />
              <Text size="md" weight={600} c="dark.8">
                {toTitleCase(modelName)} - {currentStepTitle} ({actualActiveStep + 1}/{totalSteps})
              </Text>
              {evaluationDetails?.phase && (
                <Badge variant="light" color={evaluationDetails.phaseColor} size="sm">
                  {evaluationDetails.phase}
                </Badge>
              )}
            </Group>
            <Tooltip label="Show assessment navigation">
              <ActionIcon 
                variant="subtle" 
                color="gray"
                onClick={onMenuClick}
              >
                <IconMenu2 size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
          
          {/* Area statistics now integrated into main view */}
          {currentAreaInfo && (
            <Group spacing="xs" align="center" style={{ flexWrap: 'wrap' }}>
              <Text size="xs" weight={500} c="dimmed">
                Current: {currentAreaInfo.currentIndex}/{currentAreaInfo.totalAreas} areas
              </Text>
            </Group>
          )}
        </Stack>
      </Card>
    );
  }

  return (
    <Card withBorder radius="md" p="lg" style={{ backgroundColor: '#f8f9fa' }}>
      <Stack spacing="md">
        {/* Simplified single-line header */}
        <Group position="apart" align="center">
          <Group spacing="sm">
            <IconProgressBolt size={24} color="#495057" />
            <Text size="lg" weight={600} c="dark.8">
              {toTitleCase(modelName)} - {currentStepTitle} ({actualActiveStep + 1}/{totalSteps})
            </Text>
          </Group>
          <Tooltip label="Show assessment overview">
            <ActionIcon 
              variant="subtle" 
              color="gray"
              onClick={onMenuClick}
            >
              <IconMenu2 size={20} />
            </ActionIcon>
          </Tooltip>
        </Group>

        {/* Area statistics now integrated into Currently Evaluating section */}

        <Divider />

        {/* Currently Evaluating Card - INFORMATIVE HORIZONTAL LAYOUT */}
        {evaluationDetails && (
          <Card 
            withBorder 
            radius="md" 
            p="sm"
            style={{ 
              backgroundColor: '#ffffff',
              border: '1px solid #e9ecef'
            }}
          >
            <Stack spacing="xs">
              {/* Header with Phase - lighter weight */}
              <Group spacing="xs" align="center" mb={2}>
                {React.cloneElement(evaluationDetails.icon, { size: 16 })}
                <Text size="sm" weight={500} c="dimmed">
                  Currently Evaluating
                </Text>
                {evaluationDetails.phase && (
                  <>
                    <Text size="xs" c="dimmed" style={{ opacity: 0.6 }}>•</Text>
                    <Badge variant="light" color={evaluationDetails.phaseColor} size="xs" radius="sm">
                      Phase: {evaluationDetails.phase}
                    </Badge>
                  </>
                )}
              </Group>
              
              {/* Horizontal Info Row - Better balanced */}
              <Group spacing="sm" align="center" style={{ flexWrap: 'nowrap' }}>
                {/* Enhanced Risk Area with status icon and counter */}
                <Group spacing={4} align="center" style={{ minWidth: 'fit-content' }}>
                  <Text size="xs" weight={500} c="dimmed">Risk Area:</Text>
                  {currentAreaInfo ? (
                    <HoverCard width={320} shadow="md" withArrow>
                      <HoverCard.Target>
                        <Group spacing={6} align="center" style={{ cursor: 'pointer' }}>
                          <Text size="sm" weight={600} c="dark" style={{ 
                            maxWidth: '120px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {evaluationDetails.area}
                          </Text>
                          {currentAreaInfo.status && getStatusIcon(currentAreaInfo.status) && (
                            <span style={{ color: getStatusIcon(currentAreaInfo.status).color }}>
                              {getStatusIcon(currentAreaInfo.status).icon}
                            </span>
                          )}
                          <Badge variant="light" color="blue" size="xs">
                            {currentAreaInfo.currentIndex}/{currentAreaInfo.totalAreas}
                          </Badge>
                          <IconInfoCircle size={12} color="#9ca3af" style={{ opacity: 0.7 }} />
                        </Group>
                      </HoverCard.Target>
                      <HoverCard.Dropdown>
                        <Stack spacing="xs">
                          <Text size="sm" weight={600}>Risk Areas Status ({currentAreaInfo.currentIndex}/{currentAreaInfo.totalAreas})</Text>
                          {currentAreaInfo.allAreas.map((area) => {
                            const statusInfo = getStatusIcon(area.status);
                            return (
                              <Group key={area.key} spacing="xs" align="center">
                                <Text size="xs" c="dimmed" style={{ minWidth: '20px' }}>
                                  {area.position}.
                                </Text>
                                {area.isActive ? (
                                  <Text size="xs" c="blue" weight={600}>→</Text>
                                ) : area.status === 'Complete' ? (
                                  <Text size="xs" c="green">✓</Text>
                                ) : (
                                  <Text size="xs" c="dimmed">-</Text>
                                )}
                                <Text 
                                  size="xs" 
                                  weight={area.isActive ? 600 : 400}
                                  c={area.isActive ? "blue" : statusInfo ? "dimmed" : "dark"}
                                  style={{ flex: 1 }}
                                >
                                  {area.name}
                                </Text>
                                {statusInfo && (
                                  <Group spacing={4} align="center">
                                    <span style={{ color: statusInfo.color }}>
                                      {statusInfo.icon}
                                    </span>
                                    <Text size="xs" c="dimmed">
                                      {area.status === 'DEFERRED' ? 'Saved for Later' : 'Not Applicable'}
                                    </Text>
                                  </Group>
                                )}
                                {!statusInfo && !area.isActive && area.status !== 'Complete' && (
                                  <Text size="xs" c="dimmed">Pending</Text>
                                )}
                                {area.isActive && (
                                  <Text size="xs" c="blue" weight={500}>(current)</Text>
                                )}
                              </Group>
                            );
                          })}
                        </Stack>
                      </HoverCard.Dropdown>
                    </HoverCard>
                  ) : (
                    <Text size="sm" weight={600} c="dark" style={{ 
                      maxWidth: '120px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {evaluationDetails.area}
                    </Text>
                  )}
                </Group>

                <Text size="xs" c="dimmed" style={{ opacity: 0.6 }}>|</Text>

                {/* Sources Section with expansion */}
                {currentSourcesInfo && (
                  <>
                    <Group spacing={4} align="center" style={{ minWidth: 'fit-content' }}>
                      <Text size="xs" weight={500} c="dimmed">Source:</Text>
                      <HoverCard width={320} shadow="md" withArrow>
                        <HoverCard.Target>
                          <Group spacing={6} align="center" style={{ cursor: 'pointer' }}>
                            <Text size="xs" weight={500} c="dark.7" style={{ 
                              maxWidth: '120px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {currentSourcesInfo.currentSource || 'Processing'}
                            </Text>
                            <Badge variant="light" color="orange" size="xs">
                              {currentSourcesInfo.currentIndex}/{currentSourcesInfo.totalSources}
                            </Badge>
                            <IconInfoCircle size={12} color="#9ca3af" style={{ opacity: 0.7 }} />
                          </Group>
                        </HoverCard.Target>
                        <HoverCard.Dropdown>
                          <Stack spacing="xs">
                            <Text size="sm" weight={600}>Sources Status ({currentSourcesInfo.currentIndex}/{currentSourcesInfo.totalSources})</Text>
                            {currentSourcesInfo.allSources.map((source) => (
                              <Group key={source.key} spacing="xs" align="center">
                                <Text size="xs" c="dimmed" style={{ minWidth: '20px' }}>
                                  {source.position}.
                                </Text>
                                {source.isActive ? (
                                  <Text size="xs" c="blue" weight={600}>→</Text>
                                ) : source.status === 'completed' ? (
                                  <Text size="xs" c="green">✓</Text>
                                ) : (
                                  <Text size="xs" c="dimmed">-</Text>
                                )}
                                <Text 
                                  size="xs" 
                                  weight={source.isActive ? 600 : 400}
                                  c={source.isActive ? "blue" : "dark"}
                                  style={{ flex: 1 }}
                                >
                                  {source.name}
                                </Text>
                                {source.isActive && (
                                  <Text size="xs" c="blue" weight={500}>(current)</Text>
                                )}
                              </Group>
                            ))}
                          </Stack>
                        </HoverCard.Dropdown>
                      </HoverCard>
                    </Group>
                    <Text size="xs" c="dimmed" style={{ opacity: 0.6 }}>|</Text>
                  </>
                )}

                {/* Risks Section with expansion */}
                {currentRisksInfo && (
                  <>
                    <Group spacing={4} align="center" style={{ minWidth: 'fit-content' }}>
                      <Text size="xs" weight={500} c="dimmed">Risk:</Text>
                      <HoverCard width={320} shadow="md" withArrow>
                        <HoverCard.Target>
                          <Group spacing={6} align="center" style={{ cursor: 'pointer' }}>
                            <Text size="xs" weight={500} c="dark.7" style={{ 
                              maxWidth: '120px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {currentRisksInfo.currentRisk || 'Processing'}
                            </Text>
                            <Badge variant="light" color="blue" size="xs">
                              {currentRisksInfo.currentIndex}/{currentRisksInfo.totalRisks}
                            </Badge>
                            <IconInfoCircle size={12} color="#9ca3af" style={{ opacity: 0.7 }} />
                          </Group>
                        </HoverCard.Target>
                        <HoverCard.Dropdown>
                          <Stack spacing="xs">
                            <Text size="sm" weight={600}>Risks Status ({currentRisksInfo.currentIndex}/{currentRisksInfo.totalRisks})</Text>
                            {currentRisksInfo.allRisks.map((risk) => (
                              <Group key={risk.key} spacing="xs" align="center">
                                <Text size="xs" c="dimmed" style={{ minWidth: '20px' }}>
                                  {risk.position}.
                                </Text>
                                {risk.isActive ? (
                                  <Text size="xs" c="blue" weight={600}>→</Text>
                                ) : risk.status === 'completed' ? (
                                  <Text size="xs" c="green">✓</Text>
                                ) : (
                                  <Text size="xs" c="dimmed">-</Text>
                                )}
                                <Text 
                                  size="xs" 
                                  weight={risk.isActive ? 600 : 400}
                                  c={risk.isActive ? "blue" : "dark"}
                                  style={{ flex: 1 }}
                                >
                                  {risk.name}
                                </Text>
                                {risk.isActive && (
                                  <Text size="xs" c="blue" weight={500}>(current)</Text>
                                )}
                              </Group>
                            ))}
                          </Stack>
                        </HoverCard.Dropdown>
                      </HoverCard>
                    </Group>
                    <Text size="xs" c="dimmed" style={{ opacity: 0.6 }}>|</Text>
                  </>
                )}

                {/* Current Step */}
                <Group spacing={4} align="center" style={{ minWidth: 'fit-content' }}>
                  <Text size="xs" weight={500} c="dimmed">Step:</Text>
                  <Text size="xs" weight={600} c="blue" style={{ 
                    maxWidth: '120px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {evaluationDetails.currentStep}
                  </Text>
                </Group>

                <Text size="xs" c="dimmed" style={{ opacity: 0.6 }}>|</Text>

                {/* Next Step */}
                <Group spacing={3} align="center" style={{ minWidth: 'fit-content' }}>
                  <Text size="xs" weight={500} c="dimmed">Next:</Text>
                  <IconArrowRight size={12} color="#51cf66" />
                  <Text size="xs" c="green" weight={500} style={{ 
                    maxWidth: '120px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {evaluationDetails.nextStep}
                  </Text>
                </Group>
              </Group>
            </Stack>
          </Card>
        )}
      </Stack>
    </Card>
  );
}